import React, { useEffect, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Box, Flex, Text, Textarea, HStack, useColorModeValue, useToast, Icon, Tabs, TabList, Tab,
  useBreakpointValue,
} from '@chakra-ui/react';
import { LuUpload, LuFileText, LuEye } from 'react-icons/lu';
import { markdownToHtml } from './markdown';
import './wiki-editor.css';

/**
 * Editor de Markdown com preview ao vivo (estilo markdownlivepreview.com):
 * fonte à esquerda, resultado renderizado à direita. Permite importar .md.
 */
const MarkdownImportModal = ({ isOpen, onClose, onInsert, onReplace, initialValue = '' }) => {
  const toast = useToast();
  const fileRef = useRef();
  const [texto, setTexto] = useState('');
  const [abaMobile, setAbaMobile] = useState(0);

  const isMobile = useBreakpointValue({ base: true, lg: false });
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const painelBg = useColorModeValue('gray.50', 'gray.900');
  const previewBg = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (isOpen) {
      setTexto(initialValue || '');
      setAbaMobile(0);
    }
  }, [isOpen, initialValue]);

  const html = markdownToHtml(texto);

  const handleArquivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!/\.(md|markdown|txt)$/i.test(file.name)) {
      toast({ title: 'Formato inválido', description: 'Selecione um arquivo .md, .markdown ou .txt.', status: 'warning', position: 'top-right' });
      return;
    }

    try {
      const conteudo = await file.text();
      setTexto(conteudo);
      toast({ title: `${file.name} carregado`, status: 'success', duration: 2000, position: 'top-right' });
    } catch {
      toast({ title: 'Erro ao ler o arquivo', status: 'error', position: 'top-right' });
    }
  };

  const aplicar = (fn) => {
    if (!texto.trim()) {
      toast({ title: 'Nada para importar', status: 'warning', duration: 2000, position: 'top-right' });
      return;
    }
    fn(html);
    onClose();
  };

  const mostrarFonte = !isMobile || abaMobile === 0;
  const mostrarPreview = !isMobile || abaMobile === 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', lg: '6xl' }} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH={{ base: '100dvh', lg: '90vh' }} display="flex" flexDirection="column">
        <ModalHeader fontSize="md">
          <HStack justify="space-between" pr={8} flexWrap="wrap" gap={2}>
            <HStack>
              <Icon as={LuFileText} />
              <Text>Markdown</Text>
            </HStack>
            <Button size="sm" leftIcon={<LuUpload />} variant="outline" colorScheme="orange" onClick={() => fileRef.current?.click()}>
              Importar arquivo .md
            </Button>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={2} flex="1" minH={0} display="flex" flexDirection="column">
          {isMobile && (
            <Tabs size="sm" variant="soft-rounded" colorScheme="orange" index={abaMobile} onChange={setAbaMobile} mb={2}>
              <TabList>
                <Tab><Icon as={LuFileText} mr={1} boxSize={3} />Markdown</Tab>
                <Tab><Icon as={LuEye} mr={1} boxSize={3} />Visualização</Tab>
              </TabList>
            </Tabs>
          )}

          <Flex gap={3} flex="1" minH={{ base: '50vh', lg: '55vh' }} direction={{ base: 'column', lg: 'row' }}>
            {mostrarFonte && (
              <Box flex="1" minW={0} display="flex" flexDirection="column">
                {!isMobile && <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>MARKDOWN</Text>}
                <Textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={'Cole aqui o conteúdo em Markdown ou importe um arquivo .md\n\n# Título\n\n**negrito**, `código`, listas, tabelas...'}
                  flex="1"
                  resize="none"
                  fontFamily="mono"
                  fontSize="sm"
                  bg={painelBg}
                  borderColor={borderColor}
                  spellCheck={false}
                />
              </Box>
            )}

            {mostrarPreview && (
              <Box flex="1" minW={0} display="flex" flexDirection="column">
                {!isMobile && <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>VISUALIZAÇÃO</Text>}
                <Box
                  flex="1"
                  overflowY="auto"
                  overflowX="auto"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="md"
                  bg={previewBg}
                  px={4}
                  py={3}
                >
                  {texto.trim() ? (
                    <Box className="wiki-editor-content wiki-viewer" dangerouslySetInnerHTML={{ __html: html }} />
                  ) : (
                    <Text fontSize="sm" color="gray.400">A visualização formatada aparece aqui.</Text>
                  )}
                </Box>
              </Box>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap={2} flexWrap="wrap">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
          {onReplace && (
            <Button size="sm" variant="outline" colorScheme="orange" onClick={() => aplicar(onReplace)}>
              Substituir conteúdo
            </Button>
          )}
          <Button size="sm" colorScheme="orange" onClick={() => aplicar(onInsert)}>
            Inserir no documento
          </Button>
        </ModalFooter>

        <input type="file" accept=".md,.markdown,.txt,text/markdown" ref={fileRef} style={{ display: 'none' }} onChange={handleArquivo} />
      </ModalContent>
    </Modal>
  );
};

export default MarkdownImportModal;
